import { createContext, useContext, useState, useCallback, ReactNode } from 'react'

export interface LetterWritingPanelState {
  patientId: string
  templateId?: string
  referralId?: string
  consultationId?: string
  providerCustomNote: string
}

export interface LetterWritingContextType {
  isOpen: boolean
  isMinimized: boolean
  patientId: string | null
  templateId: string | null
  referralId: string | null
  consultationId: string | null
  providerCustomNote: string
  openPanel: (params: {
    patientId: string
    templateId?: string
    referralId?: string
    consultationId?: string
    providerCustomNote?: string
  }) => void
  closePanel: () => void
  updateNote: (note: string) => void
  updateTemplate: (templateId: string | null) => void
  updateConsultationId: (consultationId: string | null) => void
  clearDraft: () => void
  toggleMinimize: () => void
  expandPanel: () => void
}

const LetterWritingContext = createContext<LetterWritingContextType | undefined>(undefined)

export function LetterWritingProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [patientId, setPatientId] = useState<string | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [referralId, setReferralId] = useState<string | null>(null)
  const [consultationId, setConsultationId] = useState<string | null>(null)
  const [providerCustomNote, setProviderCustomNote] = useState('')

  const openPanel = useCallback(
    (params: {
      patientId: string
      templateId?: string
      referralId?: string
      consultationId?: string
      providerCustomNote?: string
    }) => {
      setPatientId(params.patientId)
      setTemplateId(params.templateId ?? null)
      setReferralId(params.referralId ?? null)
      setConsultationId(params.consultationId ?? null)
      setProviderCustomNote(params.providerCustomNote ?? '')
      setIsOpen(true)
      setIsMinimized(false)
    },
    []
  )

  const closePanel = useCallback(() => {
    setIsOpen(false)
    setIsMinimized(false)
    setPatientId(null)
    setTemplateId(null)
    setReferralId(null)
    setConsultationId(null)
    setProviderCustomNote('')
  }, [])

  const updateNote = useCallback((note: string) => {
    setProviderCustomNote(note)
  }, [])

  const updateTemplate = useCallback((templateId: string | null) => {
    setTemplateId(templateId)
  }, [])

  const updateConsultationId = useCallback((consultationId: string | null) => {
    setConsultationId(consultationId)
  }, [])

  const clearDraft = useCallback(() => {
    setProviderCustomNote('')
  }, [])

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev)
  }, [])

  const expandPanel = useCallback(() => {
    setIsMinimized(false)
  }, [])

  return (
    <LetterWritingContext.Provider
      value={{
        isOpen,
        isMinimized,
        patientId,
        templateId,
        referralId,
        consultationId,
        providerCustomNote,
        openPanel,
        closePanel,
        updateNote,
        updateTemplate,
        updateConsultationId,
        clearDraft,
        toggleMinimize,
        expandPanel,
      }}
    >
      {children}
    </LetterWritingContext.Provider>
  )
}

export function useLetterWriting() {
  const context = useContext(LetterWritingContext)
  if (context === undefined) {
    throw new Error('useLetterWriting must be used within a LetterWritingProvider')
  }
  return context
}
